import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {AllAssetClassTypesRequested, AssetClassType, RoofStructureUpdated, selectAllAssetClassTypes} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    RoofStructure,
    RoofStructureOnServerCreated,
    selectRoofStructuresActionLoading,
    selectRoofStructureById,
    selectLastCreatedRoofStructureId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-roof-structure-edit',
    templateUrl: './roof-structure-edit.component.html',
    styleUrls: ['./roof-structure-edit.component.scss']
})


export class RoofStructureEditComponent implements OnInit, OnDestroy {

    model: RoofStructure;
    oldModel: RoofStructure;

    roofStructureForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    propertySubTypeId = 0;
    propertySubTypes: AssetClassType[] = [];
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param layoutUtilsService: LayoutUtilsService
     * @param subheaderService: SubheaderService
     * @param store: Store<AppState>
     * @param layoutConfigService: LayoutConfigService
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectRoofStructuresActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            this.readonly = data.readonly
            if (params.property_sub_type_id) {
                this.propertySubTypeId = Number(params.property_sub_type_id);
            }
            const id = params.id;
            if (id && id > 0) {
                this.store.pipe(select(selectRoofStructureById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initRoofStructure();
                    }
                });
            } else {
                this.model = new RoofStructure();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initRoofStructure();
            }
        });
        this.subscriptions.push(routeSubscription);

        this.store.dispatch(new AllAssetClassTypesRequested())
        this.store.select(selectAllAssetClassTypes).subscribe(res => {
          this.propertySubTypes = [];
          if (res) {
            this.propertySubTypes = res.filter(item => item.id == 7 || item.id == 11 || item.id == 17);
          }
        })
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initRoofStructure() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.roofStructureForm = this.fb.group({
            name: [this.model.name, Validators.required],
            property_sub_type_id: [this.model.property_sub_type_id, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Roof Structure '${
                this.model.name
                }'`;
        }

        return 'New Roof Structure';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.roofStructureForm.controls[controlName];
        const result = control.invalid && control.touched;
        return result;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.roofStructureForm.markAsPristine();
        this.roofStructureForm.markAsUntouched();
        this.roofStructureForm.updateValueAndValidity();
    }

    prepareRoofStructure(): RoofStructure {
        const controls = this.roofStructureForm.controls;
        const _model = new RoofStructure();
        _model.clear();
        _model.id = this.model.id;
        _model.name = controls.name.value;
        _model.property_sub_type_id = controls.property_sub_type_id.value;
        _model.is_hidden = this.model.is_hidden

        return _model;
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.roofStructureForm.controls;
        /** check form */
        if (this.roofStructureForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        const editedFacility = this.prepareRoofStructure();
        if (this.model.id > 0) {
            this.updateRoofStructure(editedFacility);
        } else {
            this.createRoofStructure(editedFacility);
        }
    }

    /**
     * Update model
     *
     * @param _model: RoofStructureModel
     */
    updateRoofStructure(_model: RoofStructure) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateRoofStructure: Update<RoofStructure> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new RoofStructureUpdated({
            partialRoofStructure: updateRoofStructure,
            roofStructure: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: RoofStructureModel
     */
    createRoofStructure(_model: RoofStructure) {
        this.store.dispatch(new RoofStructureOnServerCreated({roofStructure: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedRoofStructureId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0 &&
            this.roofStructureForm && this.roofStructureForm.controls.property_sub_type_id.value);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }

}
